import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiBarChart2,
  FiChevronDown,
  FiChevronRight,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiRefreshCw,
  FiX,
  FiAward,
  FiSearch,
} from "react-icons/fi";
import {
  getLeaders,
  getLeaderTeam,
  getMyTeam,
  getEmployeeHistory,
  downloadTeamReport,
  downloadTeamReportExcel,
  saveBlobAs,
} from "../api/client";
import { FiDownload, FiFileText } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import Pagination from "../components/Pagination";

// Helper function to map employee data
const mapEmployee = (e) => ({
  id: e.id,
  name: e.name,
  designation: e.designation,
  hierarchy: e.hierarchy,
  email: e.email,
  businessUnit: e.business_unit,
  score: e.latest_score_percentage,
  belt: e.belt,
  beltColor: e.belt_color,
  tier: e.tier,
  totalAttempts: e.total_attempts,
  lastAssessedAt: e.last_assessed_at,
  userId: e.user_id,
  // LMS-derived (may be null when Moodle not configured / user not in LMS)
  whiteBeltScore: e.white_belt_score,
  yellowBeltScore: e.yellow_belt_score,
  aiqScore: e.aiq_score,
  combinedAverage: e.combined_average,
});

// Format a percent score with one decimal, falling back to "—" for missing
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

// Helper function to get belt color
const getBeltColor = (belt) => {
  const belts = {
    green: "#10b981",
    yellow: "#eab308",
    red: "#ef4444",
    notAssessed: "#6b7280",
  };
  return belts[belt] || "#6b7280";
};

// Helper to count nested reports
function _countAllNested(node) {
  let count = 0;
  if (node.direct_reports) {
    count += node.direct_reports.length;
    for (const child of node.direct_reports) {
      count += _countAllNested(child);
    }
  }
  return count;
}

// Belt Badge Component
/** Compact icon button used in the leader-row download cluster.
 * - `busy` shows a "…" instead of the icon (no spinner library dep needed)
 * - styled to match the existing belt badges visually so the row stays tidy
 */
const DownloadIconButton = ({ title, busy, onClick, icon }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    disabled={busy}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "30px",
      height: "26px",
      padding: 0,
      border: "1px solid var(--border-subtle)",
      borderRadius: "6px",
      background: busy ? "var(--bg-card-hover)" : "var(--bg-card)",
      color: "var(--text-secondary)",
      cursor: busy ? "wait" : "pointer",
      transition: "background 0.15s, color 0.15s, border-color 0.15s",
    }}
    onMouseEnter={(e) => {
      if (!busy) {
        e.currentTarget.style.background = "var(--bg-card-hover)";
        e.currentTarget.style.color = "var(--text-primary)";
        e.currentTarget.style.borderColor = "var(--color-highlight)";
      }
    }}
    onMouseLeave={(e) => {
      if (!busy) {
        e.currentTarget.style.background = "var(--bg-card)";
        e.currentTarget.style.color = "var(--text-secondary)";
        e.currentTarget.style.borderColor = "var(--border-subtle)";
      }
    }}
  >
    {busy ? <span style={{ fontSize: 14, fontWeight: 700 }}>…</span> : icon}
  </button>
);

const BeltBadge = ({ count, color }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 8px",
        backgroundColor: `${color}20`,
        borderRadius: "4px",
        border: `1px solid ${color}40`,
        minWidth: "36px",
      }}
    >
      <div
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: color,
        }}
      />
      <span style={{ fontSize: "12px", fontWeight: "700", color }}>
        {count}
      </span>
    </div>
  );
};

// Employee Table Component
const EmployeeList = ({ employees, onEmployeeClick, isLoading, maxShow = 50 }) => {
  const [search, setSearch] = useState("");
  const [filterBelt, setFilterBelt] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const PAGE_SZ = 15;

  if (isLoading || !employees) {
    return (
      <div style={{ padding: "24px", textAlign: "center", color: "var(--text-secondary)" }}>
        <FiRefreshCw style={{ animation: "spin 1s linear infinite", marginRight: "8px" }} />
        Loading employees...
      </div>
    );
  }

  const filtered = employees.filter((emp) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || emp.name?.toLowerCase().includes(q) || emp.designation?.toLowerCase().includes(q) || emp.email?.toLowerCase().includes(q) || emp.businessUnit?.toLowerCase().includes(q);
    const matchesBelt = filterBelt === "all" || (filterBelt === "none" ? !emp.belt : emp.belt?.toLowerCase() === filterBelt);
    const matchesStatus = filterStatus === "all" || (filterStatus === "assessed" ? emp.score != null : emp.score == null);
    return matchesSearch && matchesBelt && matchesStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "name") cmp = (a.name || "").localeCompare(b.name || "");
    else if (sortBy === "score") cmp = (a.combinedAverage ?? a.score ?? -1) - (b.combinedAverage ?? b.score ?? -1);
    else if (sortBy === "date") cmp = new Date(a.lastAssessedAt || 0) - new Date(b.lastAssessedAt || 0);
    return sortDir === "desc" ? -cmp : cmp;
  });

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const thStyle = {
    padding: "8px 10px", fontSize: "11px", fontWeight: "700", color: "var(--text-secondary)",
    textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left", cursor: "pointer",
    userSelect: "none", borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap",
  };
  const tdStyle = { padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid var(--border-subtle)" };
  const sortIcon = (col) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div style={{ padding: "12px 16px" }}>
      {/* Search + Filters */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "10px", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: "180px",
          padding: "6px 10px", backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-md)",
        }}>
          <FiSearch size={14} style={{ color: "var(--text-secondary)", flexShrink: 0 }} />
          <input
            type="text" placeholder="Search name, designation, email..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, border: "none", background: "none", color: "var(--text-primary)", fontSize: "13px", outline: "none" }}
          />
        </div>
        <select value={filterBelt} onChange={(e) => { setFilterBelt(e.target.value); setPage(1); }} style={{
          padding: "6px 8px", fontSize: "12px", backgroundColor: "var(--bg-primary)", color: "var(--text-primary)",
          border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", cursor: "pointer",
        }}>
          <option value="all">All Belts</option>
          <option value="green">Green</option>
          <option value="yellow">Yellow</option>
          <option value="red">Red</option>
          <option value="none">No Belt</option>
        </select>
        <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={{
          padding: "6px 8px", fontSize: "12px", backgroundColor: "var(--bg-primary)", color: "var(--text-primary)",
          border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", cursor: "pointer",
        }}>
          <option value="all">All Status</option>
          <option value="assessed">Assessed</option>
          <option value="not_assessed">Not Assessed</option>
        </select>
        <span style={{ fontSize: "12px", color: "var(--text-secondary)", padding: "4px 0" }}>
          {filtered.length} of {employees.length}
        </span>
      </div>

      {/* Table */}
      <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
          <thead style={{ backgroundColor: "var(--bg-primary)" }}>
            <tr>
              <th style={thStyle} onClick={() => handleSort("name")}>Name{sortIcon("name")}</th>
              <th style={thStyle}>Designation</th>
              <th style={thStyle}>White Belt</th>
              <th style={thStyle}>Yellow Belt</th>
              <th style={thStyle}>AIQ Baseline</th>
              <th style={thStyle} onClick={() => handleSort("score")}>AIQ Average{sortIcon("score")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: "24px", textAlign: "center", color: "var(--text-secondary)", fontSize: "13px" }}>
                  No employees match your filters
                </td>
              </tr>
            ) : sorted.slice((page - 1) * PAGE_SZ, page * PAGE_SZ).map((emp) => (
              <tr
                key={emp.id}
                onClick={() => onEmployeeClick(emp)}
                style={{ cursor: "pointer", transition: "background-color 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--bg-card)"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: "700" }}>{emp.name}</div>
                  {emp.email && (
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "1px" }}>
                      {emp.email}
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, color: "var(--text-secondary)", fontSize: "12px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {emp.designation || "—"}
                </td>
                <td style={{ ...tdStyle, fontSize: "13px", color: emp.whiteBeltScore != null ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {fmtPct(emp.whiteBeltScore)}
                </td>
                <td style={{ ...tdStyle, fontSize: "13px", color: emp.yellowBeltScore != null ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {fmtPct(emp.yellowBeltScore)}
                </td>
                <td style={{ ...tdStyle, fontSize: "13px", color: emp.aiqScore != null ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {fmtPct(emp.aiqScore)}
                </td>
                <td style={{ ...tdStyle, fontWeight: "700", color: emp.combinedAverage != null ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {fmtPct(emp.combinedAverage)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={page}
          totalPages={Math.ceil(sorted.length / PAGE_SZ)}
          onPageChange={setPage}
          totalItems={sorted.length}
          pageSize={PAGE_SZ}
        />
      </div>
    </div>
  );
};

// History Modal Component
const HistoryModal = ({ employee, history, loading, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "24px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "var(--bg-primary)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "80vh",
          overflow: "auto",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px",
            borderBottom: "1px solid var(--border-subtle)",
            position: "sticky",
            top: 0,
            backgroundColor: "var(--bg-primary)",
          }}
        >
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: "20px", fontWeight: "700" }}>
              {employee.name}
            </h2>
            <p
              style={{
                margin: "0",
                fontSize: "13px",
                color: "var(--text-secondary)",
              }}
            >
              {employee.designation} • Assessment History
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--text-secondary)";
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "24px" }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
                color: "var(--text-secondary)",
              }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <FiRefreshCw size={24} />
              </motion.div>
            </div>
          ) : history?.attempts?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {history.attempts.map((attempt, idx) => {
                const prevScore =
                  idx < history.attempts.length - 1 ? history.attempts[idx + 1].score : null;
                const trend = prevScore
                  ? attempt.score > prevScore
                    ? "up"
                    : attempt.score < prevScore
                      ? "down"
                      : "flat"
                  : null;

                return (
                  <motion.div
                    key={attempt.sessionId || attempt.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    style={{
                      backgroundColor: "var(--bg-card)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "var(--radius-md)",
                      padding: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "14px",
                          fontWeight: "700",
                        }}
                      >
                        Attempt {attempt.attemptNumber}
                      </p>
                      <p
                        style={{
                          margin: "0",
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {new Date(attempt.completedAt).toLocaleDateString([], {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}{" "}
                        {new Date(attempt.completedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          textAlign: "right",
                        }}
                      >
                        <p
                          style={{
                            margin: "0",
                            fontSize: "18px",
                            fontWeight: "700",
                            color: "var(--color-highlight)",
                          }}
                        >
                          {attempt.score}%
                        </p>
                        {trend && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              justifyContent: "flex-end",
                              marginTop: "4px",
                              fontSize: "11px",
                              color:
                                trend === "up"
                                  ? "#10b981"
                                  : trend === "down"
                                    ? "#ef4444"
                                    : "var(--text-secondary)",
                            }}
                          >
                            {trend === "up" ? (
                              <FiTrendingUp size={12} />
                            ) : trend === "down" ? (
                              <FiTrendingDown size={12} />
                            ) : (
                              <FiMinus size={12} />
                            )}
                            {prevScore !== null && `vs ${prevScore}%`}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: getBeltColor(attempt.beltColor),
                        }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "32px 16px",
                color: "var(--text-secondary)",
              }}
            >
              <FiBarChart2 size={32} style={{ opacity: 0.3, marginBottom: "12px" }} />
              <p>No assessment history available</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Leadership Overview Component
const LeadershipOverview = ({ onEmployeeClick, mapEmployee }) => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLeader, setExpandedLeader] = useState(null);
  const [leaderTeams, setLeaderTeams] = useState({});
  const [teamLoading, setTeamLoading] = useState(null);
  const [depthFilter, setDepthFilter] = useState("all");
  const [leaderSearch, setLeaderSearch] = useState("");
  const [leaderPage, setLeaderPage] = useState(1);
  // Tracks which leader's report is being generated, keyed as `${id}:${kind}`
  // (kind = "pdf" | "xlsx"). Lets us spin only the button the user clicked.
  const [downloading, setDownloading] = useState(null);
  const LEADERS_PER_PAGE = 15;

  /** Generate + auto-save the team report for one leader. Identical to the
   * Reports page flow — same backend endpoint, same blob saver — just placed
   * inline on the leadership row so a leader doesn't have to navigate away. */
  const handleDownloadTeam = async (leader, kind /* "pdf" | "xlsx" */) => {
    if (downloading) return; // serialize concurrent clicks
    const key = `${leader.employee_id}:${kind}`;
    setDownloading(key);
    try {
      const res =
        kind === "xlsx"
          ? await downloadTeamReportExcel(leader.employee_id)
          : await downloadTeamReport(leader.employee_id);
      const slug = (leader.name || `team-${leader.employee_id}`)
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      saveBlobAs(res, `team-${slug}.${kind}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns blob even on errors; try to read it as text for the
      // useful message.
      let msg = detail;
      if (!msg && err?.response?.data instanceof Blob) {
        try {
          msg = await err.response.data.text();
        } catch {
          msg = null;
        }
      }
      alert(msg || "Failed to generate team report");
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    const fetchLeaders = async () => {
      try {
        const res = await getLeaders(1, 10);
        setLeaders(res.data || []);
      } catch (err) {
        console.error("Failed to load leaders:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaders();
  }, []);

  const handleToggle = async (leader) => {
    const lid = leader.employee_id;
    if (expandedLeader === lid) {
      setExpandedLeader(null);
      return;
    }
    setExpandedLeader(lid);

    if (!leaderTeams[lid]) {
      try {
        setTeamLoading(lid);
        const res = await getLeaderTeam(lid);
        const members = (res.data || []).map((emp) => mapEmployee(emp));
        setLeaderTeams((prev) => ({ ...prev, [lid]: members }));
      } catch (err) {
        console.error("Failed to load leader team:", err);
        setLeaderTeams((prev) => ({ ...prev, [lid]: [] }));
      } finally {
        setTeamLoading(null);
      }
    }
  };

  const getScoreColor = (score) => {
    if (score == null) return "#6b7280";
    if (score >= 70) return "#10b981";
    if (score >= 40) return "#eab308";
    return "#ef4444";
  };

  const beltDotSmall = (color) => {
    const map = { red: "#ef4444", yellow: "#eab308", green: "#10b981" };
    return (
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: map[color] || "#6b7280",
          flexShrink: 0,
        }}
      />
    );
  };

  const depthLabel = (hierarchy) => {
    if (!hierarchy) return "";
    const parts = hierarchy.split("/").map((p) => p.trim()).filter(Boolean);
    if (parts.length <= 1) return "C-Level";
    if (parts.length === 2) return "Director / VP";
    if (parts.length === 3) return "Manager";
    return "Team Lead";
  };

  const filteredLeaders = leaders.filter((l) => {
    if (depthFilter !== "all") {
      const parts = (l.hierarchy || "").split("/").map((p) => p.trim()).filter(Boolean);
      const depth = parts.length;
      if (depthFilter === "3+") { if (depth < 3) return false; }
      else if (String(depth) !== depthFilter) return false;
    }
    if (leaderSearch) {
      const q = leaderSearch.toLowerCase();
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.designation || "").toLowerCase().includes(q) ||
        (l.business_unit || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const maxTeam = Math.max(...filteredLeaders.map((l) => l.total_team_size), 1);
  const totalLeaderPages = Math.ceil(filteredLeaders.length / LEADERS_PER_PAGE);
  const paginatedLeaders = filteredLeaders.slice(
    (leaderPage - 1) * LEADERS_PER_PAGE,
    leaderPage * LEADERS_PER_PAGE
  );

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: "var(--bg-card)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          padding: "24px",
          marginBottom: "32px",
          textAlign: "center",
          color: "var(--text-secondary)",
        }}
      >
        Loading leadership overview...
      </motion.div>
    );
  }

  if (leaders.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
        overflow: "hidden",
        marginBottom: "32px",
      }}
    >
      <div
        style={{
          padding: "24px",
          borderBottom: "1px solid var(--border-subtle)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h2
            style={{
              margin: "0 0 4px 0",
              fontSize: "20px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiUsers size={20} style={{ color: "var(--color-highlight)" }} />
            Leadership Overview
          </h2>
          <p style={{ margin: 0, fontSize: "13px", color: "var(--text-secondary)" }}>
            Click a leader to see their direct reports and team stats
          </p>
        </div>
        {/* Search + Filter */}
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <FiSearch size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
            <input
              type="text"
              placeholder="Search leaders..."
              value={leaderSearch}
              onChange={(e) => { setLeaderSearch(e.target.value); setLeaderPage(1); }}
              style={{
                padding: "5px 10px 5px 30px",
                fontSize: "12px",
                borderRadius: "16px",
                border: "1px solid var(--border-subtle)",
                background: "transparent",
                color: "var(--text-primary)",
                outline: "none",
                width: "180px",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            {[
              { key: "all", label: "All" },
              { key: "1", label: "C-Level" },
              { key: "2", label: "Directors" },
              { key: "3+", label: "Managers" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => { setDepthFilter(f.key); setLeaderPage(1); }}
                style={{
                  padding: "4px 12px",
                  fontSize: "12px",
                  fontWeight: depthFilter === f.key ? "700" : "400",
                  borderRadius: "16px",
                  border: "1px solid var(--border-subtle)",
                  background:
                    depthFilter === f.key
                      ? "var(--color-highlight)"
                      : "transparent",
                  color:
                    depthFilter === f.key ? "#fff" : "var(--text-secondary)",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        {paginatedLeaders.map((leader, idx) => {
          const isExpanded = expandedLeader === leader.employee_id;
          const team = leaderTeams[leader.employee_id] || [];
          const isLoading = teamLoading === leader.employee_id;
          const barWidth = maxTeam > 0 ? (leader.total_team_size / maxTeam) * 100 : 0;

          return (
            <div key={leader.employee_id}>
              {/* The clickable row used to be a <button>, but nesting the new
                  per-row <button> download icons inside another <button> is
                  invalid HTML and triggers the parent toggle on any nested
                  click. Switched to a div with role+keyboard handlers — same
                  visual + a11y, valid markup, nested buttons can stopPropagation. */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleToggle(leader)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleToggle(leader);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  backgroundColor: isExpanded ? "rgba(255,255,255,0.03)" : "transparent",
                  border: "none",
                  borderBottom:
                    idx === paginatedLeaders.length - 1 && !isExpanded
                      ? "none"
                      : "1px solid var(--border-subtle)",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  transition: "background-color 0.15s",
                }}
              >
                {/* Expand icon */}
                <span style={{ flexShrink: 0, transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                  <FiChevronDown
                    size={16}
                    style={{ color: "var(--color-highlight)" }}
                  />
                </span>

                {/* Name + Designation */}
                <div style={{ minWidth: "200px", textAlign: "left" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {beltDotSmall(leader.belt_color)}
                    <p style={{ margin: 0, fontSize: "14px", fontWeight: "700" }}>
                      {leader.name}
                    </p>
                    <span
                      style={{
                        fontSize: "10px",
                        color: "var(--text-muted)",
                        background: "var(--bg-card)",
                        padding: "1px 6px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-subtle)",
                      }}
                    >
                      {depthLabel(leader.hierarchy)}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "12px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {leader.designation}
                    {leader.business_unit
                      ? ` · ${leader.business_unit}`
                      : ""}
                  </p>
                </div>

                {/* Team size bar */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      height: "16px",
                      borderRadius: "4px",
                      overflow: "hidden",
                      backgroundColor: "rgba(255,255,255,0.05)",
                    }}
                  >
                    <div
                      style={{
                        width: `${barWidth}%`,
                        height: "100%",
                        backgroundColor: getScoreColor(leader.team_avg_score),
                        borderRadius: "4px",
                        transition: "width 0.5s ease",
                        opacity: 0.6,
                      }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    flexShrink: 0,
                    alignItems: "center",
                  }}
                >
                  <div style={{ textAlign: "right", minWidth: "65px" }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "10px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Direct / Total
                    </p>
                    <p
                      style={{
                        margin: "1px 0 0 0",
                        fontSize: "14px",
                        fontWeight: "700",
                      }}
                    >
                      {leader.direct_report_count}
                      <span
                        style={{
                          fontSize: "11px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {" "}
                        / {leader.total_team_size}
                      </span>
                    </p>
                  </div>
                  <div style={{ textAlign: "right", minWidth: "60px" }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "10px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Team Avg
                    </p>
                    <p
                      style={{
                        margin: "1px 0 0 0",
                        fontSize: "14px",
                        fontWeight: "700",
                        color: getScoreColor(leader.team_avg_score),
                      }}
                    >
                      {leader.team_avg_score != null
                        ? `${leader.team_avg_score}%`
                        : "N/A"}
                    </p>
                  </div>
                  {/* Mini belt distribution */}
                  <div style={{ display: "flex", gap: "4px" }}>
                    {leader.team_green_count > 0 && (
                      <BeltBadge count={leader.team_green_count} color="#10b981" />
                    )}
                    {leader.team_yellow_count > 0 && (
                      <BeltBadge count={leader.team_yellow_count} color="#eab308" />
                    )}
                    {leader.team_red_count > 0 && (
                      <BeltBadge count={leader.team_red_count} color="#ef4444" />
                    )}
                  </div>

                  {/* Per-row download buttons — stopPropagation so the click
                      doesn't expand/collapse the row. Same backend endpoints
                      the Reports page uses (downloadTeamReport / *Excel). */}
                  <div
                    style={{ display: "flex", gap: "6px", marginLeft: "8px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DownloadIconButton
                      title={`Download ${leader.name}'s team report (PDF)`}
                      busy={downloading === `${leader.employee_id}:pdf`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadTeam(leader, "pdf");
                      }}
                      icon={<FiFileText size={14} />}
                    />
                    <DownloadIconButton
                      title={`Download ${leader.name}'s team report (Excel)`}
                      busy={downloading === `${leader.employee_id}:xlsx`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadTeam(leader, "xlsx");
                      }}
                      icon={<FiDownload size={14} />}
                    />
                  </div>
                </div>
              </div>

              {/* Expanded: show direct reports */}
              {isExpanded && (
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.02)",
                    borderBottom: "1px solid var(--border-subtle)",
                  }}
                >
                  <EmployeeList
                    employees={team}
                    onEmployeeClick={onEmployeeClick}
                    isLoading={isLoading}
                    maxShow={50}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalLeaderPages > 1 && (
        <div
          style={{
            padding: "12px 24px",
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <button
            disabled={leaderPage <= 1}
            onClick={() => setLeaderPage((p) => Math.max(1, p - 1))}
            style={{
              padding: "4px 12px",
              fontSize: "12px",
              borderRadius: "6px",
              border: "1px solid var(--border-subtle)",
              background: "transparent",
              color: leaderPage <= 1 ? "var(--text-muted)" : "var(--text-primary)",
              cursor: leaderPage <= 1 ? "default" : "pointer",
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
            Page {leaderPage} of {totalLeaderPages} ({filteredLeaders.length} leaders)
          </span>
          <button
            disabled={leaderPage >= totalLeaderPages}
            onClick={() => setLeaderPage((p) => Math.min(totalLeaderPages, p + 1))}
            style={{
              padding: "4px 12px",
              fontSize: "12px",
              borderRadius: "6px",
              border: "1px solid var(--border-subtle)",
              background: "transparent",
              color: leaderPage >= totalLeaderPages ? "var(--text-muted)" : "var(--text-primary)",
              cursor: leaderPage >= totalLeaderPages ? "default" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}
    </motion.div>
  );
};

// My Team Section Component
const MyTeamSection = ({ team, loading, onEmployeeClick, userName }) => {
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: "var(--bg-card)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          padding: "24px",
          marginBottom: "32px",
          textAlign: "center",
          color: "var(--text-secondary)",
        }}
      >
        Loading your team...
      </motion.div>
    );
  }

  if (!team || team.length === 0) {
    return null;
  }

  const assessed = team.filter(e => e.score != null);
  const avgScore = assessed.length > 0
    ? (assessed.reduce((s, e) => s + e.score, 0) / assessed.length).toFixed(1)
    : null;
  const greenCount = team.filter(e => e.beltColor === "green").length;
  const yellowCount = team.filter(e => e.beltColor === "yellow").length;
  const redCount = team.filter(e => e.beltColor === "red").length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
        overflow: "hidden",
        marginBottom: "32px",
      }}
    >
      <div style={{ padding: "24px", borderBottom: "1px solid var(--border-subtle)" }}>
        <h2 style={{ margin: "0 0 4px 0", fontSize: "20px", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" }}>
          <FiUsers size={20} style={{ color: "var(--color-highlight)" }} />
          My Team
        </h2>
        <p style={{ margin: 0, fontSize: "13px", color: "var(--text-secondary)" }}>
          {team.length} direct reports
          {avgScore ? ` • Avg Score: ${avgScore}%` : ""}
          {assessed.length > 0 ? ` • ${assessed.length} assessed` : ""}
        </p>
        {team.length > 0 && (
          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <BeltBadge count={greenCount} color="#10b981" />
            <BeltBadge count={yellowCount} color="#eab308" />
            <BeltBadge count={redCount} color="#ef4444" />
          </div>
        )}
      </div>
      <EmployeeList employees={team} onEmployeeClick={onEmployeeClick} maxShow={20} />
    </motion.div>
  );
};

// Main Page Component
const DashboardLeadership = () => {
  const { user } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [myTeam, setMyTeam] = useState(null);
  const [myTeamLoading, setMyTeamLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load My Team for C-level / managers
  useEffect(() => {
    if (user?.role === "c_level" || user?.role === "manager") {
      const fetchMyTeam = async () => {
        try {
          setMyTeamLoading(true);
          const res = await getMyTeam();
          setMyTeam((res.data || []).map(mapEmployee));
        } catch (err) {
          console.error("Failed to load my team:", err);
          setMyTeam([]);
        } finally {
          setMyTeamLoading(false);
        }
      };
      fetchMyTeam();
    }
  }, [user]);

  // Load employee history
  const handleEmployeeClick = async (employee) => {
    setSelectedEmployee(employee);
    setHistoryLoading(true);
    try {
      const res = await getEmployeeHistory(employee.id);
      const h = res.data;
      setEmployeeHistory({
        employee: h.employee,
        userName: h.user_name,
        userEmail: h.user_email,
        improvementTrend: h.improvement_trend,
        attempts: (h.attempts || []).map(a => ({
          sessionId: a.session_id,
          attemptNumber: a.attempt_number,
          score: a.score_percentage,
          totalQuestions: a.total_questions,
          rawScore: a.score,
          tier: a.tier,
          belt: a.belt,
          beltColor: a.belt_color,
          maxComplexity: a.max_complexity_reached,
          completedAt: a.completed_at,
          hasViolations: a.has_violations,
        })),
      });
    } catch (err) {
      console.error("Failed to load employee history:", err);
      setEmployeeHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "var(--bg-primary)",
        color: "var(--text-primary)",
        minHeight: "100vh",
        padding: "24px",
      }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: "20px" }}
      >
        <div>
          <h1 style={{ margin: "0 0 4px 0", fontSize: "1.75rem", fontWeight: "700" }}>
            Leadership
          </h1>
          <p style={{ margin: "0", fontSize: "13px", color: "var(--text-secondary)" }}>
            Team leaders and their direct reports
          </p>
        </div>
      </motion.div>

      {/* Leadership Overview */}
      <LeadershipOverview
        onEmployeeClick={handleEmployeeClick}
        mapEmployee={mapEmployee}
      />

      {/* My Team Section (C-Level / Manager) */}
      {(user?.role === "c_level" || user?.role === "manager") && (
        <MyTeamSection
          team={myTeam}
          loading={myTeamLoading}
          onEmployeeClick={handleEmployeeClick}
          userName={user?.name}
        />
      )}

      {/* History Modal */}
      <AnimatePresence>
        {selectedEmployee && employeeHistory && (
          <HistoryModal
            employee={selectedEmployee}
            history={employeeHistory}
            loading={historyLoading}
            onClose={() => {
              setSelectedEmployee(null);
              setEmployeeHistory(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardLeadership;
